/**
 * Delegates event handling for elements using `data-on*` attributes.
 * Walks up the DOM from the event target and invokes the first matching handler on an ancestor.
 *
 * This replaces inline event handlers (e.g., `onclick="..."`) to improve Content Security Policy (CSP) compliance
 * by removing the need for `unsafe-inline`.
 *
 * Note: This script currently uses `new Function()` to evaluate dynamic handler code, to allow reuse of JavaScript
 * previously written in inline attributes. This requires `unsafe-eval` in the CSP and should be used with caution.
 * Do not include unsanitized user input in `data-on*` attributes, as this can lead to cross-site scripting (XSS)
 * vulnerabilities.
 *
 * To fully remove `unsafe-eval` from the CSP, we will likely migrate to using `data-on*` attributes that refer to
 * named global functions, with any arguments passed via separate attributes such as `data-args`.
 */

(() => {
  const supportedEvents = [
    'submit',
    'change',
    'input',
    'focus',
    'blur',
    'keydown',
    'keypress',
    'keyup',
    'click'
  ];

  function delegateEvent(eventType) {
    document.addEventListener(eventType, event => {
      let el = event.target;

      while (el && el !== document) {
        const attr = `data-on${eventType}`;
        const handlerCode = el.getAttribute(attr);

        if (handlerCode) {
          try {
            const fn = new Function('event', 'element', handlerCode);
            const result = fn.call(el, event, el);

            // Prevent default if handler returns false (click and submit only)
            if (result === false && (eventType === 'click' || eventType === 'submit')) {
              event.preventDefault();
              event.stopPropagation();
            }

            // Even if we stopPropagation, we continue walking ancestors
            // so this handler doesn't block others in the DOM tree
          } catch (err) {
            console.error(`Error executing handler for ${attr}:`, err);
          }
        }

        el = el.parentElement;
      }
    }, true); // Use capture to catch non-bubbling events like focus/blur
  }

  supportedEvents.forEach(delegateEvent);
})();
